/**
 * Chat Assistant — connection configuration.
 *
 * Integration guide §1 (Connection & Authentication), §2 (Environment Endpoints),
 * §12 (Security Note: Query-String Tokens).
 *
 * The endpoint is read from the environment only — no URL is hardcoded here, so
 * Dev (us-west-1) and Prod (us-east-1) stay separate constants owned by
 * `.env` / `.env.prod`.
 *
 * Handoff CA-1 retired the original dev endpoint. The current verified one is
 * `7z1sl8pjnk.execute-api.us-west-1.amazonaws.com/dev`; `newlt9czke…` (us-west-1)
 * and `dqiodi1yb5…` (us-east-1) must not be used. CA-7 adds that production is
 * stage-specific and must come from the deployed production API output —
 * `ChatWebSocketUrl` may only be used once the deployed CloudFormation stack is
 * confirmed to expose it.
 *
 * CA-1 shows the connection as `new WebSocket(DEV_CHAT_WEBSOCKET_URL)`. That
 * names the endpoint, not the auth: §1 still requires the Cognito access token
 * in the query string, and the server derives the user (and enforces channel
 * ownership) from it, so `buildSocketUrl()` keeps appending it.
 */

/**
 * What the assistant calls itself.
 *
 * Asked its name on prod it answers "I'm M.E.M.O., Merko's Email Marketing
 * Officer", so that is the name on screen: a header reading something else
 * contradicts the reply directly underneath it.
 *
 * The persona lives in the backend's system prompt and no frame carries it, so
 * this is a constant that has to be kept in step by hand. It is in one place so
 * that is a single edit — and the backend has been asked for a field to carry
 * it instead.
 */
export const ASSISTANT_NAME = "M.E.M.O.";

/** The one-line role it gives alongside the name. */
export const ASSISTANT_ROLE = "Merko's Email Marketing Officer";

/** Raw endpoint from the environment, without the `?token=` query string. */
export const CHAT_WS_ENDPOINT: string = (process.env.REACT_APP_CHAT_WS_URL || "").trim();

/** True when the app has a usable WebSocket endpoint configured. */
export const isChatAssistantConfigured = (): boolean => CHAT_WS_ENDPOINT.length > 0;

/**
 * How the socket authenticates.
 *
 *   `token`  — guide §1, the current flow: the Cognito **access token** goes in
 *              the query string, with no pre-flight request.
 *   `ticket` — guide §3, the legacy flow: POST `/chat-assistant-ticket` first and
 *              connect with `?ticket=`.
 *
 * The guide is explicit that the ticket endpoint should be ignored for the
 * token-based integration (§3, and §14 "Do not call the legacy ticket
 * endpoint"), so `token` is the default and `ticket` must be switched on
 * deliberately via `REACT_APP_CHAT_WS_AUTH_MODE=ticket`. It exists as an escape
 * hatch for an environment still running the old server flow — not as something
 * the app falls back to on its own.
 */
export type SocketAuthMode = "token" | "ticket";

export const CHAT_WS_AUTH_MODE: SocketAuthMode =
    (process.env.REACT_APP_CHAT_WS_AUTH_MODE || "").trim().toLowerCase() === "ticket"
        ? "ticket"
        : "token";

/** Query-string parameter carrying the credential, per mode. */
export const AUTH_QUERY_PARAM: Record<SocketAuthMode, string> = {
    token: "token",
    ticket: "ticket",
};

/**
 * Build the full connection URL.
 *
 * Guide §1: "Connect directly to the WebSocket endpoint and pass the Cognito
 * access token in the query string. There is no pre-flight HTTP request and no
 * ticket exchange in the current flow."
 */
export function buildSocketUrl(
    credential: string,
    mode: SocketAuthMode = CHAT_WS_AUTH_MODE,
): string {
    if (!CHAT_WS_ENDPOINT) {
        throw new Error(
            "REACT_APP_CHAT_WS_URL is not configured. Set it to the environment's " +
            "API Gateway WebSocket endpoint (without a token query string).",
        );
    }
    const separator = CHAT_WS_ENDPOINT.includes("?") ? "&" : "?";
    const param = AUTH_QUERY_PARAM[mode];
    return `${CHAT_WS_ENDPOINT}${separator}${param}=${encodeURIComponent(credential)}`;
}

/**
 * Strip the access token out of a connection URL.
 *
 * Guide §12 Security Note: the token travels in the URL, so complete connection
 * URLs must never reach console logs, monitoring tools, or error reports.
 * Every log line in this feature routes through here.
 */
export function redactToken(url: string): string {
    // Both credential kinds are secrets; neither may reach a log sink.
    return url.replace(/([?&](?:token|ticket)=)[^&]*/gi, "$1<redacted>");
}

/** Reconnect tuning — guide §12 "bounded exponential backoff with jitter". */
export const RECONNECT = {
    /** First retry delay, in ms. */
    baseDelayMs: 1_000,
    /** Upper bound on the backoff curve, in ms. */
    maxDelayMs: 30_000,
    /**
     * Consecutive connect-time rejections tolerated before the client gives up
     * and reports `auth_failed`. One retry happens after a forced token refresh
     * (guide §10 "Refresh the Cognito access token and reconnect"); if the
     * refreshed token is rejected too, the credentials are genuinely bad.
     */
    maxAuthRetries: 2,
} as const;

/**
 * Compute the next backoff delay with equal jitter, so a fleet of tabs coming
 * back from a network blip does not reconnect in lockstep.
 */
export function backoffDelay(attempt: number): number {
    const capped = Math.min(RECONNECT.baseDelayMs * 2 ** attempt, RECONNECT.maxDelayMs);
    return Math.round(capped / 2 + Math.random() * (capped / 2));
}
